import { Link } from "react-router-dom";
import { ArrowUpRight, CheckCircle2, CalendarClock } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { SERVICES, SERVICE_CATEGORIES } from "@/data/services";

export default function ServicesIndex() {
  const byId = Object.fromEntries(SERVICES.map((s) => [s.id, s]));

  return (
    <>
      <Header />
      <main className="pt-36 sm:pt-40 pb-24 bg-gradient-to-b from-[#FAFAFA] via-white to-[#F4F7FB]" data-testid="services-index">
        {/* Hero */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs font-bold tracking-[0.3em] uppercase text-[#06B6D4]">Nos services</div>
          <h1 className="display mt-3 text-4xl sm:text-5xl lg:text-6xl text-[#0A1128] font-extrabold leading-[1.05]">
            7 leviers d'accompagnement, <span className="text-[#64748B] font-light italic">classés par moment d'intervention.</span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-[#475569] max-w-3xl">
            Que vous prépariez votre projet, lanciez votre entreprise ou cherchiez à scaler — chaque service est positionné là où il a le plus d'impact.
          </p>
        </section>

        {/* Groupes par catégorie */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-14 space-y-16">
          {SERVICE_CATEGORIES.map((cat) => (
            <div key={cat.key} data-testid={`category-${cat.key}`}>
              <div className="flex items-center gap-3 mb-6">
                <span
                  className="inline-block w-2.5 h-12 rounded-full"
                  style={{ background: cat.color }}
                  aria-hidden
                />
                <div>
                  <div className="text-xs font-bold tracking-[0.3em] uppercase text-[#0A1128]">{cat.title}</div>
                  <h2 className="display text-2xl sm:text-3xl text-[#0A1128] font-extrabold mt-1">{cat.desc}</h2>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {cat.service_ids.map((sid) => {
                  const s = byId[sid];
                  if (!s) return null;
                  const Icon = s.icon;
                  return (
                    <Link
                      key={s.id}
                      to={`/services/${s.id}`}
                      data-testid={`catalogue-service-${s.id}`}
                      className="group relative bg-white border border-black/8 rounded-3xl p-6 hover:border-[#06B6D4]/40 hover:shadow-2xl transition-all overflow-hidden"
                    >
                      {/* Decorative blob */}
                      <div
                        className="absolute -top-12 -right-8 w-40 h-40 rounded-full blur-3xl opacity-30 group-hover:opacity-50 transition-opacity"
                        style={{ background: s.color }}
                        aria-hidden
                      />
                      <div className="relative">
                        <div className="flex items-start justify-between gap-3">
                          <div
                            className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                            style={{ background: s.color }}
                          >
                            <Icon size={22} className="text-[#0A1128]" />
                          </div>
                          <span className="display text-3xl sm:text-4xl font-black text-transparent" style={{ WebkitTextStroke: "1.5px " + s.color }}>
                            {s.n}
                          </span>
                        </div>

                        <div className="mt-4">
                          <div className="display text-xl font-extrabold text-[#0A1128]">{s.short}</div>
                          <p className="mt-1.5 text-sm text-[#475569] leading-relaxed">{s.desc}</p>
                        </div>

                        <ul className="mt-4 space-y-1.5">
                          {s.items.slice(0, 3).map((it) => (
                            <li key={it} className="flex items-start gap-2 text-[13px] text-[#0A1128]">
                              <CheckCircle2 size={14} className="text-[#06B6D4] mt-0.5 flex-shrink-0" />
                              <span>{it}</span>
                            </li>
                          ))}
                          {s.items.length > 3 && (
                            <li className="text-[12px] text-[#64748B] pl-6">+ {s.items.length - 3} autre(s)…</li>
                          )}
                        </ul>

                        <div className="mt-5 inline-flex items-center gap-1 text-sm font-bold text-[#06B6D4] group-hover:gap-2 transition-all">
                          Découvrir <ArrowUpRight size={14} />
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </section>

        {/* CTA bandeau */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-20">
          <div className="bg-[#0A1128] text-white rounded-3xl p-8 sm:p-12 relative overflow-hidden">
            <div className="absolute -top-20 -right-16 w-72 h-72 rounded-full blur-3xl opacity-30" style={{ background: "#06B6D4" }} />
            <div className="relative grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
              <div>
                <div className="text-xs font-bold tracking-[0.3em] uppercase text-[#67E8F9]">Pas sûr du bon levier ?</div>
                <h2 className="display text-3xl sm:text-4xl font-extrabold mt-3">Démarrez par un RDV découverte gratuit.</h2>
                <p className="text-white/75 mt-3">15 minutes par téléphone ou un café à Istres — on identifie ensemble le service qui crée le plus de valeur pour vous.</p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Link to="/rendez-vous" data-testid="catalogue-cta-rdv" className="inline-flex items-center gap-2 bg-[#06B6D4] hover:bg-[#67E8F9] text-[#0A1128] font-bold px-6 py-3 rounded-full transition-colors">
                  <CalendarClock size={16} />Prendre RDV
                </Link>
                <Link to="/portail/connexion" data-testid="catalogue-cta-espace" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/30 text-white font-bold px-6 py-3 rounded-full transition-colors">
                  Espace Entreprise <ArrowUpRight size={14} />
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
